import { AnimatePresence, motion } from "framer-motion";
import { useEffect } from "react";
import confetti from "canvas-confetti";
import { formatSim, heatColor } from "../lib/heat";

interface Props {
  open: boolean;
  outcome: "won" | "lost" | "revealed";
  target: string | null;
  sourceWord?: string | null;
  bestSimilarity: number;
  guessCount: number;
  onPlayAgain: () => void;
  onClose: () => void;
}

export function ResultModal({
  open,
  outcome,
  target,
  sourceWord,
  bestSimilarity,
  guessCount,
  onPlayAgain,
  onClose,
}: Props) {
  useEffect(() => {
    if (open && outcome === "won") {
      confetti({
        particleCount: 130,
        spread: 80,
        origin: { y: 0.6 },
        colors: ["#a78bfa", "#38bdf8", "#fbbf24", "#fb7185"],
      });
    }
  }, [open, outcome]);

  const title =
    outcome === "won" ? "You got it!" : outcome === "lost" ? "Time's up" : "Revealed";
  const subtitle =
    outcome === "won"
      ? `In ${guessCount} ${guessCount === 1 ? "guess" : "guesses"}`
      : `Best similarity — ${formatSim(bestSimilarity)}`;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-30 flex items-center justify-center p-4 bg-ink-950/70 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.92, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.94, opacity: 0 }}
            transition={{ type: "spring", stiffness: 220, damping: 22 }}
            className="glass rounded-3xl p-8 max-w-md w-full text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="text-xs uppercase tracking-[0.2em] mb-2"
              style={{ color: heatColor(outcome === "won" ? 1 : bestSimilarity) }}
            >
              {outcome === "won" ? "✦ exact match ✦" : "round over"}
            </div>
            <h2 className="font-display text-3xl mb-1">{title}</h2>
            <p className="text-ink-300 text-sm mb-6">{subtitle}</p>
            {target && (
              <div className="mb-6">
                <div className="text-xs text-ink-400 uppercase tracking-wider mb-1">target</div>
                <div className="font-mono text-lg text-ink-100 px-4 py-3 glass-soft rounded-xl">
                  {target}
                </div>
                {sourceWord && (
                  <div className="text-xs text-ink-400 mt-2">
                    seed word: <span className="font-mono text-ink-200">{sourceWord}</span>
                  </div>
                )}
              </div>
            )}
            <div className="flex gap-2 justify-center">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-xl text-ink-300 hover:text-ink-100 hover:bg-white/5"
              >
                review
              </button>
              <button
                onClick={onPlayAgain}
                className="px-5 py-2 rounded-xl bg-accent text-ink-950 font-medium hover:opacity-90"
              >
                play again
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
